import React, { useState, useCallback, useEffect, useRef } from "react"
import { Button } from "@ciq/shared/components/buttons"
import { ErrorToast } from "@ciq/shared/utils/toast"
import { useAppContext } from "@ciq/shared/hooks/context"
import { FileDropZone } from "./FileDropZone"
import { FileTypeModal } from "./FileTypeModal"
import { UploadedFilesList } from "./UploadedFilesList"
import { createUploadJob, uploadFileToS3, submitJob, cancelJob } from "./api"
import "../../../../css/ZoneUploads.css"

/**
 * @typedef {Object} StagedFile
 * @property {string} filename - Original filename
 * @property {File} file - The actual File object for upload
 */

/**
 * @typedef {Object} ZoneUploadManagerProps
 * @property {(jobId: string) => void} onProcessingStart - Callback when processing begins
 */

/**
 * Manages the zone data file upload workflow.
 * Handles file selection, type modal, S3 upload, and job submission.
 * Gets schoolId from app context.
 *
 * @param {ZoneUploadManagerProps} props - Component props
 * @returns {React.ReactElement} The upload manager component
 */
export function ZoneUploadManager({ onProcessingStart }) {
  const { schoolId } = useAppContext()
  const [files, setFiles] = useState({
    room_to_zone: null,
    space_inventory: null
  })
  const [pendingFiles, setPendingFiles] = useState([])
  const [batchStagedTypes, setBatchStagedTypes] = useState([])
  const [showTypeModal, setShowTypeModal] = useState(false)
  const [jobId, setJobId] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState(null)
  const processButtonRef = useRef(null)
  const prevBothFilesUploaded = useRef(false)

  const uploadedTypes = Object.entries(files)
    .filter(([_, file]) => file !== null)
    .map(([type]) => type)

  const bothFilesUploaded = files.room_to_zone !== null && files.space_inventory !== null

  // Scroll to Process Files button when both files become uploaded
  useEffect(() => {
    if (bothFilesUploaded && !prevBothFilesUploaded.current) {
      processButtonRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
    prevBothFilesUploaded.current = bothFilesUploaded
  }, [bothFilesUploaded])

  // Prevent browser from opening/downloading files when dropped outside the drop zone
  useEffect(() => {
    const preventDefaultDrop = (event) => {
      event.preventDefault()
      event.stopPropagation()
    }

    document.addEventListener("dragover", preventDefaultDrop)
    document.addEventListener("drop", preventDefaultDrop)

    return () => {
      document.removeEventListener("dragover", preventDefaultDrop)
      document.removeEventListener("drop", preventDefaultDrop)
    }
  }, [])

  const handleFilesSelect = useCallback((selectedFiles) => {
    setError(null)

    // Get available slots
    const availableTypes = Object.entries(files)
      .filter(([_, f]) => f === null)
      .map(([type]) => type)

    // Check if too many files
    if (selectedFiles.length > availableTypes.length) {
      const maxFiles = availableTypes.length
      if (maxFiles === 0) {
        setError("Both file types are already selected.")
        ErrorToast("Both file types are already selected.")
        return
      }
      const errorMsg = `Only ${maxFiles} file slot${maxFiles === 1 ? "" : "s"} available. Please select ${maxFiles} file${maxFiles === 1 ? "" : "s"}.`
      setError(errorMsg)
      ErrorToast(errorMsg)
      return
    }

    // If one slot available, auto-assign
    if (availableTypes.length === 1) {
      setFiles(prev => ({
        ...prev,
        [availableTypes[0]]: {
          filename: selectedFiles[0].name,
          file: selectedFiles[0]
        }
      }))
      return
    }

    // Show modal to let user choose type for first file, queue the rest
    setPendingFiles(selectedFiles)
    setBatchStagedTypes([])
    setShowTypeModal(true)
  }, [files])

  const handleTypeSelect = useCallback((fileType) => {
    if (pendingFiles.length === 0) return

    const currentFile = pendingFiles[0]
    const remainingFiles = pendingFiles.slice(1)

    // Store the file locally - upload happens when Process Files is clicked
    setFiles(prev => ({
      ...prev,
      [fileType]: {
        filename: currentFile.name,
        file: currentFile
      }
    }))

    // Track this type as staged in the current batch
    setBatchStagedTypes(prev => [...prev, fileType])

    // If there are remaining files, keep modal open for next file
    if (remainingFiles.length > 0) {
      setPendingFiles(remainingFiles)
      // Modal stays open, will show next file
    } else {
      setShowTypeModal(false)
      setPendingFiles([])
      setBatchStagedTypes([])
    }
  }, [pendingFiles])

  const handleTypeModalCancel = useCallback(() => {
    // Revert any files staged during this batch
    if (batchStagedTypes.length > 0) {
      setFiles(prev => {
        const newFiles = { ...prev }
        for (const fileType of batchStagedTypes) {
          newFiles[fileType] = null
        }
        return newFiles
      })
    }

    setShowTypeModal(false)
    setPendingFiles([])
    setBatchStagedTypes([])
  }, [batchStagedTypes])

  const handleDelete = useCallback((fileType) => {
    setFiles(prev => ({
      ...prev,
      [fileType]: null
    }))
  }, [])

  const handleProcessFiles = useCallback(async () => {
    if (!bothFilesUploaded) return

    setIsUploading(true)
    setError(null)

    let createdJobId = null

    try {
      // Prepare files array for job creation
      const filesToUpload = Object.entries(files)
        .filter(([_, stagedFile]) => stagedFile !== null)
        .map(([fileType, stagedFile]) => ({
          filename: stagedFile.filename,
          fileType: fileType,
          file: stagedFile.file
        }))

      // 1. Create job and get presigned URLs
      const job = await createUploadJob(schoolId, filesToUpload)
      createdJobId = job.job_id
      setJobId(job.job_id)

      // 2. Upload files to S3 in parallel
      await Promise.all(
        job.uploads.map(upload => {
          const fileData = filesToUpload.find(f => f.fileType === upload.file_type)
          return uploadFileToS3(upload.upload_url, fileData.file)
        })
      )

      // 3. Submit job for processing
      await submitJob(schoolId, job.job_id)
      onProcessingStart(job.job_id)
    } catch (err) {
      // If a job was created but upload/submit failed, cancel it to allow retry
      if (createdJobId) {
        try {
          await cancelJob(schoolId, createdJobId)
        } catch (cancelErr) {
          // Log but don't block - job may already be in non-cancellable state
          console.error("Failed to cancel job after error:", cancelErr)
        }
      }
      setError("Failed to process files. Please try again.")
      ErrorToast("Failed to process files. Please try again.")
      setJobId(null)
      setIsUploading(false)
    }
  }, [bothFilesUploaded, schoolId, files, onProcessingStart])

  const handleError = useCallback((errorMessage) => {
    setError(errorMessage)
    ErrorToast(errorMessage)
  }, [])

  const handleDismissError = useCallback(() => {
    setError(null)
  }, [])

  return (
    <div className="zone-upload-manager">
      <div className="zone-upload-manager__header">
        <h2 className="zone-upload-manager__title">Upload Zone Data</h2>

        <FileDropZone
          onFilesSelect={handleFilesSelect}
          onError={handleError}
          disabled={isUploading || bothFilesUploaded}
        />
      </div>

      <FileTypeModal
        show={showTypeModal}
        filename={pendingFiles[0]?.name || ""}
        uploadedTypes={uploadedTypes}
        onSelect={handleTypeSelect}
        onCancel={handleTypeModalCancel}
      />

      <UploadedFilesList
        files={files}
        onDelete={handleDelete}
      />

      {error && (
        <div className="zone-upload-manager__error">
          <span>{error}</span>
          <button
            className="zone-upload-manager__error-dismiss"
            onClick={handleDismissError}
            aria-label="Dismiss error"
          >
            &times;
          </button>
        </div>
      )}

      <div className="zone-upload-manager__footer">
        <div className="zone-upload-manager__help">
          <p>
            {bothFilesUploaded
              ? "Both files selected. Ready to process zone data."
              : "Select both Room to Zone Alignment and Space Inventory files to begin processing."}
          </p>
        </div>

        <div className="zone-upload-manager__actions" ref={processButtonRef}>
          <Button
            variant="primary"
            disabled={!bothFilesUploaded || isUploading}
            onClick={handleProcessFiles}
          >
            {isUploading ? "Processing..." : "Process Files"}
          </Button>
        </div>
      </div>
    </div>
  )
}
